import { apiClient, idempotentConfig, unwrap } from './client';
import type {
  ApiEnvelope,
  AssignResult,
  DispositionWorkItemResult,
  WorkItemDetail,
  WorkItemPage,
  WorkItemQueue,
  WorkItemStatus,
  WorkItemType,
} from './types';

/**
 * Work-items — the investigator workbench feed (PS-3 cross-track contract). Track A builds these
 * in parallel; the SPA degrades to teaching empty states (errors.ts `isNotAvailable`) when absent.
 *
 * Disposition `action` is per-type:
 *  - HUMAN_TASK   → a decision-option token (payload = the signal body the option emits)
 *  - SIGNAL_REVIEW→ CLEAR | ESCALATE | LINK_ENTITY
 *  - APPROVAL     → server answers 409 USE_APPROVALS_API (deep-link to the PS-2 approval UX instead)
 *  - RFI_FOLLOWUP → RESPONSE_RECEIVED | EXPIRE | CANCEL
 */

const enc = (s: string) => encodeURIComponent(s);

export interface WorkItemQuery {
  queue?: WorkItemQueue;
  status?: WorkItemStatus;
  type?: WorkItemType;
  entityId?: string;
  page?: number;
  pageSize?: number;
}

/** GET /v1/work-items — the queue feed, filterable by queue / status / type / entity. */
export async function listWorkItems(q: WorkItemQuery = {}): Promise<WorkItemPage> {
  const { data } = await apiClient.get<ApiEnvelope<WorkItemPage>>('/v1/work-items', {
    params: {
      queue: q.queue,
      status: q.status,
      type: q.type,
      entityId: q.entityId,
      page: q.page,
      pageSize: q.pageSize,
    },
  });
  return unwrap(data);
}

/** GET /v1/work-items/{id} — the item plus its per-type context bundle. */
export async function getWorkItem(itemId: string): Promise<WorkItemDetail> {
  const { data } = await apiClient.get<ApiEnvelope<WorkItemDetail>>(
    `/v1/work-items/${enc(itemId)}`,
  );
  return unwrap(data);
}

/**
 * POST /v1/work-items/{id}/assign — assign to `assignee`, or self-assign when absent.
 * 409 `ALREADY_DONE` when the item was completed/claimed out from under the caller.
 */
export async function assignWorkItem(
  itemId: string,
  assignee?: string,
  idemKey?: string,
): Promise<AssignResult> {
  const { data } = await apiClient.post<ApiEnvelope<AssignResult>>(
    `/v1/work-items/${enc(itemId)}/assign`,
    assignee ? { assignee } : {},
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/**
 * POST /v1/work-items/{id}/disposition — resolve the item.
 * APPROVAL items answer 409 `USE_APPROVALS_API` (handle by deep-linking to the designer/PublishDrawer).
 */
export async function dispositionWorkItem(
  itemId: string,
  action: string,
  opts: { note?: string; payload?: Record<string, unknown>; idemKey?: string } = {},
): Promise<DispositionWorkItemResult> {
  const { data } = await apiClient.post<ApiEnvelope<DispositionWorkItemResult>>(
    `/v1/work-items/${enc(itemId)}/disposition`,
    { action, note: opts.note, payload: opts.payload },
    idempotentConfig(opts.idemKey),
  );
  return unwrap(data);
}
